import React, { Suspense, type JSX } from "react";
import type { LocationLink } from "@roll1/shared";
import CanvasArea from "./CanvasArea";
import type { SessionCanvasController } from "../hooks/useSessionCanvasController";
import type { UseInpaintingReturn } from "../hooks/useInpainting";
import type { UseDetailPassReturn } from "../hooks/useDetailPass";
import type {
  UseOutpaintingReturn,
  OutpaintDirection,
} from "../hooks/useOutpainting";

const DrawingManager = React.lazy(() => import("./domain/DrawingManager"));
const FogManager = React.lazy(() => import("./domain/FogManager"));
const NoteManager = React.lazy(() => import("./domain/NoteManager"));
const TokenManager = React.lazy(() => import("./domain/TokenManager"));
const DragDropManager = React.lazy(() => import("./domain/DragDropManager"));
const WallToolManager = React.lazy(() => import("./domain/WallToolManager"));
const PinManager = React.lazy(() => import("./domain/PinManager"));
const TextToolManager = React.lazy(() => import("./domain/TextToolManager"));
const LocationLinkManager = React.lazy(() => import("./domain/LocationLinkManager"));
const MapObjectsManager = React.lazy(() => import("./domain/MapObjectsManager"));

/**
 * SessionCanvas — the interactive canvas cluster (domain Managers + CanvasArea),
 * extracted from AppShell so BOTH the table and GM Studio drive one identical
 * canvas. This is the provider-LESS half: the provider trio (ActorProvider /
 * DrawingSelectionProvider / PageManager) stays with the CALLER, because AppShell
 * has non-cluster consumers of those providers (Sidebar → PageOps) that must keep
 * them outer — a self-contained variant could never slim AppShell without nesting.
 *
 * The feeding wiring is in `useSessionCanvasController` (the caller calls it once
 * and passes the result in, so it can also feed DrawingSelectionProvider). The
 * only piece that differs per host is `pageId` (edit-target: table = active page,
 * studio = previewed page) — WallToolManager takes it as a prop; the other
 * Managers pick it up via `useEditPageId()` (studio provides EditPageContext).
 *
 * Layout/container is the caller's: this renders a fragment; the host sizes
 * CanvasArea. StudioGate hard-swaps table ⇄ studio; Studio can mount two map
 * panes, each under its own canvas providers (StudioMapCanvas, GM Studio
 * package 5.3) — every cluster drives the engine of its own providers.
 */
export interface SessionCanvasProps {
  controller: SessionCanvasController;
  /** Edit-target page — table: outgoingPageId; studio: previewed page. */
  pageId: string;
  onOpenSheet?: (id: string) => void;
  onOpenJournalEntry?: (entryId: string) => void;
  onContextMenuLink?: (link: LocationLink, x: number, y: number) => void;
  // AI add-ons — table passes them; studio omits (guarded on isGM/map already).
  inpainting?: UseInpaintingReturn | null;
  detailPass?: UseDetailPassReturn | null;
  outpainting?: UseOutpaintingReturn | null;
  onOpenOutpaint?: (direction: OutpaintDirection) => void;
  directSheetId?: string | null;
  onClearDirectSheetId?: () => void;
  /** Table-only combat HUD. Studio passes false (prep surface, not play). */
  showCharacterHud?: boolean;
}

export default function SessionCanvas({
  controller,
  pageId,
  onOpenSheet,
  onOpenJournalEntry,
  onContextMenuLink,
  inpainting = null,
  detailPass = null,
  outpainting = null,
  onOpenOutpaint,
  directSheetId = null,
  onClearDirectSheetId,
  showCharacterHud = true,
}: SessionCanvasProps): JSX.Element {
  // TokenManager requires an onOpenSheet; studio omits it (no HUD sheet overlay).
  const openSheet = onOpenSheet ?? (() => {});
  return (
    <>
      <Suspense fallback={null}>
        <DrawingManager />
        <FogManager />
        <NoteManager />
        <WallToolManager pageId={pageId} />
        <TokenManager onOpenSheet={openSheet} />
        <DragDropManager />
        <PinManager onOpenJournalEntry={onOpenJournalEntry} />
      </Suspense>
      <CanvasArea
        drawingSelection={controller.drawingSelection}
        aoeTemplates={controller.aoeTemplates.templates}
        aoeAffectedTokenIds={controller.aoeAffectedTokenIds}
        aoePlacement={controller.aoePlacement}
        linkDrawMode={controller.linkDrawMode}
        onContextMenuLink={onContextMenuLink}
        getTokenDescription={controller.getTokenDescription}
        avatarSize={controller.avatarSize}
        onAvatarSizeChange={controller.onAvatarSizeChange}
        inpainting={inpainting}
        detailPass={detailPass}
        outpainting={outpainting}
        onOpenOutpaint={onOpenOutpaint}
        onOpenSheet={onOpenSheet}
        directSheetId={directSheetId}
        onClearDirectSheetId={onClearDirectSheetId}
        showCharacterHud={showCharacterHud}
      />
      <Suspense fallback={null}>
        <TextToolManager />
        <LocationLinkManager />
        <MapObjectsManager />
      </Suspense>
    </>
  );
}
